import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  LEAD_STATUS_DOT_COLORS,
  LEAD_STATUS_LABELS,
} from "@/features/lead/constants";
import { LeadCard } from "@/features/lead/components/lead-card";
import type { Lead, LeadStatus } from "@/features/lead/types";

interface LeadColumnProps {
  status: LeadStatus;
  leads: Lead[];
  enableDrag: boolean;
  onOpenLead: (lead: Lead) => void;
  onAddLead: (status: LeadStatus) => void;
  /**
   * Status-UI variant passed straight through to each {@link LeadCard}.
   * Defaults to `"select"` (the in-card dropdown) for backwards
   * compatibility; the mobile kanban passes `"pill"` so cards render
   * the tappable status pill + kebab in the top-right instead.
   */
  cardStatusUI?: "select" | "pill";
}

export function LeadColumn({
  status,
  leads,
  enableDrag,
  onOpenLead,
  onAddLead,
  cardStatusUI = "select",
}: LeadColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `column-${status}`,
    data: { type: "column", status },
  });

  return (
    <section
      ref={setNodeRef}
      aria-label={`Cột ${LEAD_STATUS_LABELS[status]}`}
      className={cn(
        "flex min-h-[200px] w-full shrink-0 flex-col gap-2 rounded-xl border border-border bg-card p-2.5",
        isOver ? "border-primary/40 ring-2 ring-primary/10" : "",
      )}
    >
      <header className="flex items-center justify-between px-1">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              "size-2 shrink-0 rounded-full",
              LEAD_STATUS_DOT_COLORS[status],
            )}
            aria-hidden="true"
          />
          <span className="text-[12px] font-semibold">
            {LEAD_STATUS_LABELS[status]}
          </span>
          <span className="font-mono text-[11px] text-ink-faint tabular-nums">
            {leads.length}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onAddLead(status)}
          aria-label={`Thêm khách mới vào ${LEAD_STATUS_LABELS[status]}`}
          className="inline-flex size-5 items-center justify-center rounded-md text-ink-faint hover:bg-muted hover:text-foreground"
        >
          <Plus className="size-3.5" aria-hidden="true" />
        </button>
      </header>

      <SortableContext
        items={leads.map((l) => `lead-${l.id}`)}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-2">
          {leads.length === 0 ? (
            <p className="rounded-md border border-dashed border-border py-3 text-center font-mono text-[11px] text-ink-faint">
              Trống
            </p>
          ) : (
            leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                enableDrag={enableDrag}
                onOpen={onOpenLead}
                statusUI={cardStatusUI}
              />
            ))
          )}
        </div>
      </SortableContext>
    </section>
  );
}
